import { defineStore } from 'pinia'
import { useSupabase } from './supabase'
import { ref } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export type FriendshipStatus = 'none' | 'pending' | 'friends' | 'incoming'

export const useFriends = defineStore('friends', () => {
  const supabase = useSupabase()
  const myFriends = ref<
    {
      avatar: string
      photos_count: number
      current_streak: number
      trophy_count: number
      user_id: string
      username: string
    }[]
  >([])

  async function getMyFriends() {
    if (supabase.user.status !== 'SignedIn') return
    myFriends.value = await getFriends(supabase.user.data?.id)
  }

  function isMyFriend(userId: string): boolean {
    if (supabase.user.status !== 'SignedIn') return false
    if (supabase.user.data?.id === userId) return false
    return myFriends.value.some((friend) => friend.user_id === userId)
  }

  async function getFriendshipStatus(userId: string): Promise<FriendshipStatus | null> {
    if (supabase.user.status !== 'SignedIn') return null
    if (supabase.user.data?.id === userId) return null
    const { data, error } = await supabase.api
      .from('friends')
      .select('*')
      .or(
        `and(user_id_1.eq.${supabase.user.data?.id},user_id_2.eq.${userId}),and(user_id_1.eq.${userId},user_id_2.eq.${supabase.user.data?.id})`
      )

    if (error && error.code !== 'PGRST116') {
      // PGRST116 = no rows found
      console.error(error)
      throw error
    }

    if (!data || data.length === 0) return 'none'

    if (data[0].status === 'accepted') return 'friends'
    if (data[0].user_id_1 === supabase.user.data?.id) return 'pending'
    return 'incoming'
  }

  async function addFriend(userId: string) {
    if (supabase.user.status !== 'SignedIn') return
    const { error } = await supabase.api
      .from('friends')
      .insert({ user_id_1: supabase.user.data?.id, user_id_2: userId, status: 'pending' })
    if (error) {
      console.error(error)
      throw error
    }
  }

  async function acceptFriend(userId: string) {
    if (supabase.user.status !== 'SignedIn') return
    const { error } = await supabase.api
      .from('friends')
      .update({ status: 'accepted' })
      .eq('status', 'pending')
      .or(
        `and(user_id_1.eq.${supabase.user.data?.id},user_id_2.eq.${userId}),and(user_id_1.eq.${userId},user_id_2.eq.${supabase.user.data?.id})`
      )
    if (error) {
      console.error(error)
      throw error
    }
    await getMyFriends()
  }

  async function removeFriend(userId: string) {
    if (supabase.user.status !== 'SignedIn') return
    const { error } = await supabase.api
      .from('friends')
      .delete()
      .or(
        `and(user_id_1.eq.${supabase.user.data?.id},user_id_2.eq.${userId}),and(user_id_1.eq.${userId},user_id_2.eq.${supabase.user.data?.id})`
      )
    if (error) {
      console.error(error)
      throw error
    }
    await getMyFriends()
  }

  const pendingFriendInvites = ref<{
    sent: any[]
    received: any[]
  }>({ sent: [], received: [] })

  async function getFriendInvites() {
    if (supabase.user.status !== 'SignedIn') return
    const currentUserId = supabase.user.data.id
    const { data, error } = await supabase.api
      .from('friends')
      .select(
        '*, user_id_1:user_settings!friends_user_id_1_fkey(user_id, username, avatar), user_id_2:user_settings!friends_user_id_2_fkey(user_id, username, avatar)'
      )
      .or(`user_id_1.eq.${currentUserId},user_id_2.eq.${currentUserId}`)
      .eq('status', 'pending')

    if (error) {
      console.error(error)
      throw error
    }

    if (data) {
      pendingFriendInvites.value = {
        sent: data.filter((invite: any) => invite.user_id_1.user_id === currentUserId),
        received: data.filter((invite: any) => invite.user_id_2.user_id === currentUserId)
      }
    }
  }

  async function getFriends(user_id: string): Promise<
    {
      avatar: string
      photos_count: number
      current_streak: number
      trophy_count: number
      user_id: string
      username: string
    }[]
  > {
    let friends: {
      avatar: string
      photos_count: number
      current_streak: number
      trophy_count: number
      user_id: string
      username: string
    }[] = []

    const { data, error } = await supabase.api
      .from('friends')
      .select(
        '*, user_id_1:user_settings!friends_user_id_1_fkey(user_id, username, avatar), user_id_2:user_settings!friends_user_id_2_fkey(user_id, username, avatar)'
      )
      .eq('status', 'accepted')
      .or(`user_id_1.eq.${user_id},user_id_2.eq.${user_id}`)

    if (error) {
      console.error(error)
      throw error
    }

    if (data) {
      friends = data
        .map(
          (friendship: {
            created_at: Date
            id: number
            status: 'accepted'
            user_id_1: {
              avatar: string
              user_id: string
              username: string
            }
            user_id_2: {
              avatar: string
              user_id: string
              username: string
            }
          }) => {
            if (friendship.user_id_1.user_id === user_id) {
              return {
                ...friendship.user_id_2,
                photos_count: 0,
                current_streak: 0,
                trophy_count: 0
              }
            } else {
              return {
                ...friendship.user_id_1,
                photos_count: 0,
                current_streak: 0,
                trophy_count: 0
              }
            }
          }
        )
        .toSorted((a, b) => a.username.localeCompare(b.username))

      for (const friend of friends) {
        const stats = await getFriendStats(friend.user_id)
        friend.photos_count = stats.photos_count
        friend.current_streak = stats.current_streak
        friend.trophy_count = stats.trophy_count
      }
    }

    if (supabase.user.status === 'SignedIn' && user_id === supabase.user.data.id) {
      myFriends.value = friends
    }

    return friends
  }

  async function getFriendStats(userId: string) {
    const { data, error } = await supabase.api
      .from('profiles')
      .select('current_streak, trophy_count, photos_count')
      .eq('user_id', userId)
      .single()

    if (error) {
      console.error(error)
      throw error
    }

    return data
  }

  return {
    pendingFriendInvites,
    getFriendshipStatus,
    addFriend,
    acceptFriend,
    removeFriend,
    getFriendInvites,
    getFriends,
    getMyFriends,
    myFriends,
    isMyFriend
  }
})
